import { get, orderBy } from 'lodash';
import moment from 'moment';
import { PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature, useLocalize } from '@treams/treams-hooks';
import { ActivityIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { AnalyticsDetailSortOrder } from '@treams/treams-network';
import {
	Avatar,
	Empty,
	GraphQLErrorMessage,
	ITimeMachine,
	Skeleton,
	Spinner,
	Table,
} from '@treams/treams-ui';

import { displayFloat } from '../metrics/AnalyticsNumberMetric';

type TableCellValue = {
	rawValue?: string | number | Date;
	renderValue: ReactNode;
	excel?: {
		type?: StringConstructor | NumberConstructor | DateConstructor;
		format?: string;
		value?: { value: number; total: number } | string | number | Date;
	};
};

const TableContainerSpinnerOverlay = styled(Box)`
	position: absolute;
	width: 100%;
	height: 100%;

	display: flex;
	align-items: center;
	justify-content: center;
`;

const TableContainer = (props: PropsWithChildren<{ loading: boolean }>) => {
	return (
		<div
			style={{
				pointerEvents: props.loading ? 'none' : 'inherit',
				position: 'relative',
				opacity: props.loading ? '0.60' : '1',
			}}
		>
			{props.loading && (
				<TableContainerSpinnerOverlay>
					<Spinner />
				</TableContainerSpinnerOverlay>
			)}
			{props.children}
		</div>
	);
};

function toExcelLocalDate(date: Date): Date {
	return new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
}

const tableCellOverrides: Record<
	string,
	(row: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>) => TableCellValue
> = {
	'retention.contractLength': (row) => {
		if (
			row.startDate?.__typename !== 'AnalyticsDetailDate' ||
			row.endDate?.__typename !== 'AnalyticsDetailDate'
		) {
			return { renderValue: <>&mdash;</> };
		}

		const startDate = moment(row.startDate.date);
		const endDate = moment(row.endDate.date);

		const diffInMs = endDate.diff(startDate);

		const rawValue = diffInMs;

		const diffInMonths = endDate.diff(startDate, 'months');

		if (diffInMonths === 0) {
			return {
				rawValue,
				renderValue: moment.duration(diffInMs).humanize(),
			};
		}

		const diffInYears = endDate.diff(startDate, 'years');

		if (diffInYears === 0) {
			return {
				rawValue,
				renderValue: moment.duration(diffInMonths - diffInYears * 12, 'months').humanize(),
			};
		}

		const remainderInMonths = diffInMonths - diffInYears * 12;

		return {
			rawValue,
			renderValue: [
				moment.duration(diffInYears, 'years').humanize(),
				...(remainderInMonths > 0
					? [moment.duration(diffInMonths - diffInYears * 12, 'months').humanize()]
					: []),
			].join(', '),
		};
	},
};

export function tableCellValue(
	localize: (item: graphql.LocalizedString) => { languageID: string; text: string },
	row: Record<string, graphql.AnalyticsDetailColumnValue | null | undefined>,
	header: graphql.AnalyticsDetailHeader,
	sortOrder?: 'asc' | 'desc',
): TableCellValue {
	const value = get(row, header.value);

	if (header.type === 'list') {
		const list = row[header.key] as graphql.AnalyticsDetailList;

		if (!list || list.__typename !== 'AnalyticsDetailList' || list.items.length === 0) {
			return {
				renderValue: <>&mdash;</>,
			};
		}

		const values = list.items.map((item) => {
			const newRow =
				header.key === 'departments'
					? { department: item }
					: header.key === 'managers' || header.key === 'currentManagers'
						? { user: item }
						: {};

			return tableCellValue(localize, newRow, { ...header, type: undefined });
		});

		const ordered = orderBy(values ?? [], (item) => item.rawValue, sortOrder);

		return {
			rawValue: ordered[0].rawValue,
			renderValue: ordered.map((item) => item.renderValue).join(', '),
		};
	}

	if (tableCellOverrides[header.value]) {
		return tableCellOverrides[header.value](row);
	}

	if (value?.__typename === 'AnalyticsDetailInt') {
		return {
			rawValue: value.integer,
			renderValue: value.integer,
			excel: {
				type: Number,
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailFloat') {
		return {
			rawValue: value.float,
			renderValue: value.float,
			excel: {
				type: Number,
				format: '0.00',
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailPercentage') {
		return {
			rawValue: value.percentage,
			renderValue: `${displayFloat(value.percentage * 100)}%`,
			excel: {
				type: Number,
				format: '0.0%',
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailFraction') {
		return {
			rawValue: value.value === 0 && value.total === 0 ? undefined : value.value / value.total,
			renderValue:
				value.value === 0 && value.total === 0 ? <>&mdash;</> : `${value.value} / ${value.total}`,
			excel: {
				value: { value: value.value, total: value.total },
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailText') {
		return {
			rawValue: value.text.toLowerCase(),
			renderValue: value.text,
			excel: {
				type: String,
				value: value.text,
			},
		};
	}

	if (value?.__typename === 'AnalyticsDetailDate') {
		const date = moment(value.date);
		const jsDate = date.toDate();

		return {
			rawValue: jsDate,
			renderValue: date.format('LL'),
			excel: {
				type: Date,
				value: toExcelLocalDate(jsDate),
				format: 'd mmmm yyyy',
			},
		};
	}

	if (header.type === 'date' && value) {
		const date = moment(value as unknown as string);
		const jsDate = date.toDate();

		return {
			rawValue: jsDate,
			renderValue: date.format('LL'),
			excel: {
				type: Date,
				value: toExcelLocalDate(jsDate),
				format: 'd mmmm yyyy',
			},
		};
	}

	if (value && typeof value !== 'object') {
		return {
			rawValue: value,
			renderValue: value,
		};
	}

	if (value && Array.isArray(value) && 'languageID' in value[0]) {
		const text = localize(value).text;
		return {
			rawValue: text.toLowerCase(),
			renderValue: text,
			excel: {
				type: String,
				value: text,
			},
		};
	}

	return {
		renderValue: <>&mdash;</>,
		excel: {
			type: String,
		},
	};
}

function TableHeader(props: {
	headers: graphql.AnalyticsDetailHeader[];
	sortedBy: number;
	sortOrder: 'asc' | 'desc' | undefined;
	setSorting: (index: number, order: 'asc' | 'desc') => void;
}) {
	const { t } = useTranslation();

	const onSortClick = (index: number) => {
		if (props.sortedBy === index) {
			props.setSorting(index, props.sortOrder === 'asc' ? 'desc' : 'asc');
			return;
		}
		props.setSorting(index, 'asc');
	};

	return (
		<Table.Header>
			{props.headers.map((header, index) => {
				if (header.detailViewOnly) {
					return null;
				}
				return (
					<Table.Cell
						basis={1}
						key={header.key}
						sorted={props.sortedBy === index ? props.sortOrder : undefined}
						onClick={
							header.sortable
								? () => {
										onSortClick(index);
									}
								: undefined
						}
					>
						{header.label || t(`analytics.metric.details.${header.key}`)}
					</Table.Cell>
				);
			})}
		</Table.Header>
	);
}

export default function AnalyticsMetricDetailsTable(props: {
	id: string;
	tableID: string;
	type: graphql.AnalyticsType;
	filters: graphql.AnalyticsFiltersV2;
	timeMachine: ITimeMachine;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const hasFeedbackOnCompetentiesCount = useFeature(
		graphql.FeatureName.AnalyticsFeedbackOnCompetencies,
	);

	const [fetch, { loading, error }] = graphql.useAnalyticsMetricDetailsTableLazyQuery();

	const [page, setPage] = useState<graphql.AnalyticsDetail | undefined>(undefined);

	const fetchPage = async (pageIndex: number, sortByIndex: number, sortOrder?: 'asc' | 'desc') => {
		const sortByKey = (page && page.headers[sortByIndex]?.key) || undefined;
		const queryResult = await fetch({
			variables: {
				id: props.id,
				filters: props.filters,
				type: props.type,
				pageIndex: pageIndex,
				pageSize: 10,
				sortOrder:
					sortOrder === 'asc' ? AnalyticsDetailSortOrder.Asc : AnalyticsDetailSortOrder.Desc,
				sortBy: sortByKey,
				tableID: props.tableID,
			},
		});

		const newPage = (queryResult.data?.analytics.detail as graphql.AnalyticsDetail) || undefined;

		setPage({
			...newPage,
			headers: hasFeedbackOnCompetentiesCount
				? newPage.headers
				: newPage.headers.filter((header) => header.key !== 'feedbackOnCompetentiesCount'),
		});
	};

	const sortByIndex = (page?.headers || []).findIndex((h) => {
		return h.key === page?.sortBy;
	});

	const onSortUpdated = (newSortByIndex: number, newSortOrder: 'asc' | 'desc') => {
		fetchPage(page?.pageIndex || 0, newSortByIndex, newSortOrder);
	};

	const onUpdatePageIndex = (newPageIndex: number) => {
		fetchPage(newPageIndex, sortByIndex, page?.sortOrder);
	};

	useEffect(() => {
		setPage(undefined);
		fetchPage(0, 0, undefined);
	}, [props.id]);

	const rows =
		(page?.rows as graphql.AnalyticsDetailRow[])?.map((row) => {
			return Object.fromEntries(row.columns.map((column) => [column.key, column.value]));
		}) ?? [];

	const hasRows = page && page.rows.length > 0;
	const hasZeroRows = page && page.rows.length == 0;

	return (
		<>
			{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

			{hasRows && (
				<TableContainer loading={loading}>
					<Table>
						<TableHeader
							headers={page.headers}
							sortOrder={page.sortOrder}
							sortedBy={sortByIndex}
							setSorting={onSortUpdated}
						/>
						{rows.map((row, index) => (
							<Table.Row key={index}>
								{page.headers
									.filter((header) => !header.detailViewOnly)
									.map((header) => (
										<Table.Cell basis={1} key={`${header.key}-${index}`}>
											{header.key === 'user' && (
												<Avatar mr={2} user={row.user as graphql.User} size={20} flexShrink={0} />
											)}
											{tableCellValue(localize, row, header).renderValue}
										</Table.Cell>
									))}
							</Table.Row>
						))}
					</Table>
					<Table.Pagination
						pagination={{
							currentPage: page.pageIndex + 1,
							perPage: page.pageSize,
							max: page.totalRows,
						}}
						disabled={{
							next: (page.pageIndex + 1) * page.pageSize >= page.totalRows,
							previous: page.pageIndex <= 0,
						}}
						onNext={() => {
							onUpdatePageIndex(page.pageIndex + 1);
						}}
						onPrevious={() => {
							onUpdatePageIndex(page.pageIndex - 1);
						}}
					/>
				</TableContainer>
			)}

			{!loading && hasZeroRows && (
				<Table>
					<TableHeader
						headers={page.headers}
						sortOrder={page.sortOrder}
						sortedBy={sortByIndex}
						setSorting={onSortUpdated}
					/>
					<Skeleton.Empty
						contents={
							<Empty
								icon={<ActivityIcon />}
								title={t('analytics.metric.empty.title')}
								description={t('analytics.metric.empty.description')}
							/>
						}
					>
						<Skeleton.Repeat count={5}>
							<Table.Row>
								<Skeleton.Repeat count={page.headers.length}>
									<Table.Cell.Skeleton basis={1} />
								</Skeleton.Repeat>
							</Table.Row>
						</Skeleton.Repeat>
					</Skeleton.Empty>
				</Table>
			)}

			{loading && (!page || hasZeroRows) && (
				<Table>
					<Table.Header>
						<Table.Cell.Skeleton basis={0.33} />
						<Table.Cell.Skeleton basis={0.33} />
						<Table.Cell.Skeleton basis={0.33} />
					</Table.Header>
					<Skeleton.Repeat count={3}>
						<Table.Row>
							<Table.Cell.Skeleton basis={0.33} />
							<Table.Cell.Skeleton basis={0.33} />
							<Table.Cell.Skeleton basis={0.33} />
						</Table.Row>
					</Skeleton.Repeat>
				</Table>
			)}
		</>
	);
}
